// src/components/SidebarAccordion.js
import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronRight, FaChevronDown, FaFolder, FaFolderOpen } from 'react-icons/fa';
import { SidebarContext } from '../context/SidebarContext';
import './SidebarAccordion.css';

// One entry within the tree: a plain link leaf if it has no `children`, or
// its own independently-expandable sub-section if it does - recursing into
// itself again for grandchildren, so a menu can nest to any depth (e.g.
// Archives > Series > OSM/RLP/CAD > Browse per Candidate/Browse by Centre).
// Styled like a classic file-explorer tree: a rotating chevron, each branch
// expanding independently rather than closing its siblings. The top level
// (level 0 - OSM, Archives, Active Series, etc.) keeps its own meaningful
// icon, matching how "This PC" keeps its own icon in a real file-explorer
// tree; only genuine grouping folders below it (a series name, "browse by
// X") get the generic folder icon, since they have no more specific one.
function MenuNode({ item, level, onLinkClick }) {
  const [open, setOpen] = useState(false);
  const hasChildren = !!item.children && item.children.length > 0;

  if (!hasChildren) {
    return (
      <li className={`nested-level-${level}`}>
        <Link to={item.path || '#'} className="d-block sidebar-link" onClick={onLinkClick}>
          {item.icon && <span className="me-2">{item.icon}</span>}
          {item.title}
        </Link>
      </li>
    );
  }

  const branchIcon = level === 0 ? item.icon : (open ? <FaFolderOpen /> : <FaFolder />);

  return (
    <li className={`nested-item nested-level-${level}`}>
      <button
        className={`nested-toggle tree-branch nested-level-${level}-toggle ${open ? '' : 'collapsed'}`}
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="tree-chevron">{open ? <FaChevronDown /> : <FaChevronRight />}</span>
        {branchIcon && <span className="me-2 tree-folder-icon">{branchIcon}</span>}
        {item.title}
      </button>
      {open && (
        <ul className="list-unstyled mb-0 nested-list">
          {item.children.map((child, i) => (
            <MenuNode key={i} item={child} level={level + 1} onLinkClick={onLinkClick} />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function SidebarAccordion({ menuItems, onLinkClick, treeStyle = false }) {
  const { sidebarOpen } = useContext(SidebarContext);
  const [activeIndex, setActiveIndex] = useState(null);

  // Explorer-style tree throughout, including the top level: every section
  // expands independently (unlike the accordion below, which closes
  // whichever section was previously open). Opt-in via a prop rather than
  // the default, so existing menus elsewhere keep their current look
  // unless a layout deliberately asks for this style.
  if (treeStyle) {
    return (
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="accordion tree-root" id="sidebarAccordion">
          <ul className="list-unstyled mb-0">
            {menuItems.map((menu, index) => (
              <MenuNode key={index} item={menu} level={0} onLinkClick={onLinkClick} />
            ))}
          </ul>
        </div>
      </div>
    );
  }

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
      <div className="accordion" id="sidebarAccordion">
        {menuItems.map((menu, index) => {
          const hasChildren = !!menu.children;
          return (
            <div key={index} className="accordion-item">
              {hasChildren ? (
                <>
                  <h2 className="accordion-header" id={`heading-${index}`}>
                    <button
                      className={`accordion-button ${activeIndex === index ? '' : 'collapsed'}`}
                      type="button"
                      onClick={() => toggleAccordion(index)}
                      aria-expanded={activeIndex === index}
                      aria-controls={`collapse-${index}`}
                    >
                      {menu.icon && <span className="me-2">{menu.icon}</span>}
                      {menu.title}
                    </button>
                  </h2>
                  <div
                    id={`collapse-${index}`}
                    className={`accordion-collapse collapse ${activeIndex === index ? 'show' : ''}`}
                    aria-labelledby={`heading-${index}`}
                    data-bs-parent="#sidebarAccordion"
                  >
                    <div className="accordion-body p-0">
                      <ul className="list-unstyled mb-0">
                        {menu.children.map((child, cIndex) => (
                          <MenuNode key={cIndex} item={child} level={1} onLinkClick={onLinkClick} />
                        ))}
                      </ul>
                    </div>
                  </div>
                </>
              ) : (
                <Link
                  to={menu.path || '#'}
                  className="d-block p-2 sidebar-link accordion-button standalone-link"
                  style={{ border: 'none' }}
                  onClick={onLinkClick}
                >
                  {menu.icon && <span className="me-2">{menu.icon}</span>}
                  {menu.title}
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
